import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import {
  GET_USER_NOTIFICATIONS,
  MARK_NOTIFICATIONS_READ,
} from "../graphql/agentQueries";
import { BellIcon } from "../icons";
import NotificationDetailModal from "./NotificationDetailModal/NotificationDetailModal";
import {
  isMarketing,
  getListingPath,
  getHistoryPath,
  getStoredRoleName,
} from "../utils/notificationNav";

const BELL_VISIBLE_LIMIT = 3;
const COUNT_THRESHOLD = 10;

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selected, setSelected] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { companyId } = useParams();

  const { data, loading, refetch } = useQuery(GET_USER_NOTIFICATIONS, {
    fetchPolicy: "network-only",
    pollInterval: 30000, // Poll every 30 seconds
  });

  const [markAsRead] = useMutation(MARK_NOTIFICATIONS_READ, {
    onCompleted() {
      refetch();
    },
  });

  useEffect(() => {
    if (data?.getUserNotifications) {
      setNotifications(data.getUserNotifications);
    }
  }, [data]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleMarkAllRead = () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n._id);
    if (unreadIds.length > 0) {
      markAsRead({ variables: { notificationIds: unreadIds } });
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      markAsRead({ variables: { notificationIds: [notification._id] } });
    }

    // Marketing (broadcast) → open read-only detail modal, no navigation.
    if (isMarketing(notification)) {
      setSelected(notification);
      setIsOpen(false);
      return;
    }

    // System-generated → navigate to the linked listing (role-aware path).
    if (notification.listing?._id && companyId) {
      const roleName = getStoredRoleName();
      navigate(getListingPath(companyId, notification.listing._id, roleName));
    }

    setIsOpen(false);
  };

  const handleCloseModal = () => setSelected(null);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(parseInt(timestamp, 10));
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "LISTING_FAVORITED":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            className="w-5 h-5 text-red-500"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "NEW_LISTING_MATCH":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            className="w-5 h-5 text-green-500"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            className="w-5 h-5 text-blue-500"
            fill="currentColor"
          >
            <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
          </svg>
        );
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-full hover:bg-gray-100 focus:outline-none"
        title="Notifications"
      >
        <BellIcon className="w-6 h-6 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center min-w-5 h-5 px-1 text-[10px] leading-none font-bold text-white bg-red-500 rounded-full">
            {unreadCount > COUNT_THRESHOLD ? `${COUNT_THRESHOLD}+` : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-sm text-primary hover:text-primary/80"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                No notifications
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.slice(0, BELL_VISIBLE_LIMIT).map((notification) => (
                  <div
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !notification.read ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm ${
                            !notification.read
                              ? "font-semibold text-gray-900"
                              : "text-gray-700"
                          }`}
                        >
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {notification.body}
                        </p>
                        {notification.listing && (
                          <p className="text-xs text-gray-400 mt-1">
                            {notification.listing.streetAddr},{" "}
                            {notification.listing.city}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {getTimeAgo(notification.createdAt)}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer — "View all" → context-aware history page */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate(getHistoryPath(companyId, getStoredRoleName()));
                }}
                className="text-sm font-semibold text-primary hover:text-primary_dark"
              >
                View all notifications →
              </button>
            </div>
          )}
        </div>
      )}

      {selected && (
        <NotificationDetailModal
          notification={selected}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default NotificationBell;
